<template>
  <v-slide-x-reverse-transition>
    <div
      class="chatbot"
      v-if="isChatBotVisible"
      :class="{ 'chatbot--mobile': isMobile }"
    >
      <div class="chatbot__header">
        <icon-base icon="custom/ai" class="pr-2" />
        Ask CY

        <v-spacer />

        <v-btn icon class="mr-1" @click="isChatBotVisible = false">
          <v-icon color="light">close</v-icon>
        </v-btn>
      </div>

      <div class="chatbot__body" ref="messagesContainer">
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="chatbot__message"
          :class="`chatbot__message--${message.role}`"
        >
          <div
            class="chatbot__message-content"
            v-html="messageContent(message)"
          ></div>
        </div>

        <transition name="feedback-fade" mode="out-in">
          <div v-if="isFeedbackOpen" class="chatbot__feedback">
            <div class="chatbot__feedback-message" v-if="isFeedbackSent">
              Thank you for your feedback!
            </div>

            <div class="chatbot__feedback-message" v-else>
              Was this message helpful?
              <div class="chatbot__feedback-actions">
                <v-btn
                  icon
                  small
                  color="appMain"
                  class="mr-2"
                  :disabled="isFeedbackSending"
                  @click="sendBotFeedback(1)"
                >
                  <v-icon small>thumb_up</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="appMain"
                  class="mr-1"
                  :disabled="isFeedbackSending"
                  @click="sendBotFeedback(0)"
                >
                  <v-icon small>thumb_down</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <div class="chatbot__form">
        <v-progress-linear
          v-if="isLoading"
          height="3"
          indeterminate
          color="appMain"
          class="chatbot__progress"
        />

        <v-textarea
          ref="messageInput"
          placeholder="Type your message..."
          class="no-label"
          hide-details
          rows="4"
          auto-grow
          v-model="userQuery"
          :disabled="isLoading"
          @keyup.enter="sendMessage"
        />

        <v-btn
          icon
          color="appMain"
          class="chatbot__form-send"
          :disabled="!userQuery || isLoading"
          @click="sendMessage"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </div>
    </div>
  </v-slide-x-reverse-transition>
</template>

<script>
import { vueEnv } from '@/globals';
import qs from 'qs';
import EventBus from '@/factories/eventBus';

const domain = vueEnv('chatbot_path');

export default {
  name: 'CustomerSupportChatBot',
  data() {
    return {
      isChatBotVisible: false,
      isLoading: false,
      userQuery: '',
      messages: [],
      isFirstChunk: false,
      responseId: null,
      typingBuffer: '',
      typingInterval: null,

      // feedback
      isFeedbackVisible: false,
      isFeedbackSending: false,
      isFeedbackSent: false,
    };
  },
  computed: {
    isFeedbackOpen() {
      return this.isFeedbackVisible && !this.typingBuffer;
    },
  },
  watch: {
    isFeedbackOpen(newVal) {
      if (!newVal) return;

      setTimeout(() => {
        this.scrollToBottom();
      }, 200);
    },

    isChatBotVisible(isVisible) {
      if (!isVisible) return;

      if (!this.isMobile) {
        this.focusTextarea();
      }

      setTimeout(() => {
        this.scrollToBottom(true);
      }, 100);
    },

    '$route.hash': {
      handler(newHash) {
        if (newHash === '#cy' && !this.isChatBotVisible) {
          this.openChatBot();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async focusTextarea() {
      await this.$nextTick();
      this.$refs.messageInput?.$el.querySelector('textarea')?.focus();
    },

    async sendMessage() {
      const userQuery = this.userQuery.trim();
      if (!userQuery) return;

      this.addUserMessage(userQuery);
      this.clearFeedback();

      try {
        this.isLoading = true;
        const params = qs.stringify(
          {
            q: userQuery,
            previousResponseId: this.responseId,
          },
          {
            skipNulls: true,
          }
        );

        const response = await fetch(`${domain}/v1/?${params}`);
        await this.handleStreamedResponse(response);
      } catch (error) {
        console.error('Error during fetch:', error);
        this.isLoading = false;
      }

      this.scrollToBottom();
    },

    async addUserMessage(userQuery) {
      await this.sleep(300);
      this.messages.push({
        role: 'user',
        content: userQuery,
      });
      this.userQuery = null;
      this.scrollToBottom();
    },

    async handleStreamedResponse(response) {
      if (!response.body) throw new Error('No response body found');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let partial = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          partial += decoder.decode(value, { stream: true });
          let lines = partial.split('\n');

          partial = lines.pop();

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6));
                this.processStreamEvent(event);
              } catch (e) {
                console.error('Failed to parse stream event:', e, line);
                continue;
              }
            }
          }
        }
      } catch (error) {
        console.error('Error reading stream:', error);
        this.isLoading = false;
        throw error;
      } finally {
        this.finalizeMessage();
      }
    },

    processStreamEvent(event) {
      if (!event?.type) {
        console.warn('Received event without type:', event);
        return;
      }

      const { type } = event;

      switch (type) {
        case 'response.created':
        case 'response.in_progress':
          this.responseId = event.response?.id;
          break;

        case 'response.output_item.added':
          if (!event.item?.id) {
            console.warn('Received output_item.added without item.id:', event);
            return;
          }
          this.messages.push({
            role: 'bot',
            content: '',
            itemId: event.item.id,
          });
          break;

        case 'response.output_text.delta':
          if (!event.item_id || !event.delta) {
            console.warn(
              'Received output_text.delta without required fields:',
              event
            );
            return;
          }
          this.updateBotMessageById(event.item_id, event.delta);
          break;

        case 'response.output_item.done':
        case 'response.completed':
          this.finalizeMessage();
          break;

        default:
          break;
      }

      this.scrollToBottom();
    },

    updateBotMessageById(itemId, delta) {
      const msg = this.messages.find(
        (m) => m.itemId === itemId && m.role === 'bot'
      );

      if (!msg) return;

      this.typingBuffer += delta;

      if (!this.typingInterval) {
        this.typingInterval = setInterval(() => {
          if (this.typingBuffer.length > 0) {
            msg.content += this.typingBuffer[0];
            this.typingBuffer = this.typingBuffer.slice(1);
            this.scrollToBottom();
          } else {
            clearInterval(this.typingInterval);
            this.typingInterval = null;
            this.scrollToBottom();
            this.focusTextarea();
          }
        }, 10);
      }
    },

    finalizeMessage() {
      this.isLoading = false;
      this.isFeedbackVisible = true;
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async sendBotFeedback(wasHelpful) {
      try {
        this.isFeedbackSending = true;
        await fetch(`${domain}/submit-feedback?id=${this.responseId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ was_helpful: wasHelpful }),
        });

        this.isFeedbackSent = true;

        setTimeout(() => {
          this.clearFeedback();
        }, 2000);
      } catch (error) {
        console.error('Error sending feedback:', error);
      } finally {
        this.isFeedbackSending = false;
      }
    },

    clearFeedback() {
      this.isFeedbackVisible = false;
      this.isFeedbackSent = false;
    },

    messageContent(message) {
      return message.role === 'bot'
        ? this.formatMarkdown(message.content)
        : message.content;
    },

    formatMarkdown(content) {
      return content
        .replace(
          /(#+)\s(.+)/g,
          (match, hashes, text) =>
            `<h${hashes.length}>${text}</h${hashes.length}>`
        )
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/_(.*?)_/g, '<em>$1</em>')
        .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>')
        .replace(/\n/g, '<br>')
        .replace(/^- (.+)/gm, '<ul><li>$1</li></ul>')
        .replace(/^\d+\.\s(.+)/gm, '<ol><li>$1</li></ol>');
    },

    async scrollToBottom(force = false) {
      await this.$nextTick();
      const tolerance = 60;
      const messagesContainer = this.$refs.messagesContainer;

      if (!messagesContainer) return;

      const isScrollAtBottom =
        messagesContainer.scrollTop + messagesContainer.clientHeight >=
        messagesContainer.scrollHeight - tolerance;

      if (!isScrollAtBottom && !force) return;

      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    },

    openChatBot(search) {
      this.isChatBotVisible = true;

      if (search) {
        this.userQuery = search;
        this.sendMessage();

        setTimeout(() => {
          this.scrollToBottom(true);
        }, 300);
      }
    },
  },
  created() {
    EventBus.$on('openChatBot', this.openChatBot);

    EventBus.$on('closeChatBot', () => {
      if (this.isChatBotVisible) {
        this.isChatBotVisible = false;
      }
    });
  },
};
</script>

<style lang="less">
.chatbot {
  position: fixed;
  bottom: 0;
  right: 0;
  margin: 20px;
  width: 440px;
  max-width: calc(100% - 40px);
  max-height: calc(100% - 40px);
  height: 580px;
  min-height: 100px;
  border-radius: @base-radius;
  box-shadow: @base-shadow4;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 2147483600; // max z-index

  &--mobile {
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
  }

  &__header {
    padding: 8px 8px 8px 16px;
    background: var(--gradient);
    font-family: var(--v-headers-font-base);
    font-weight: 600;
    display: flex;
    align-items: center;
    color: #fff;
    font-size: 20px;
    box-shadow: @base-shadow2;
    z-index: 1;

    svg {
      fill: #fff;
    }
  }

  &__body {
    flex: 1;
    background: #f7f7f7;
    padding: 16px;
    overflow: auto;
  }

  &__form {
    display: flex;
    border-top: 1px solid #ccc;
    position: relative;
    background: #fff;
    box-shadow: 0px -4px 8px rgba(35, 55, 79, 0.12) !important;

    .v-input__slot {
      margin-bottom: 0 !important;
      padding-right: 30px !important;
    }

    &-send {
      position: absolute !important;
      bottom: 6px;
      right: 6px;
    }
  }

  &__progress {
    position: absolute;
    top: -3px;
    left: 0;
    right: 0;
    width: 100%;
  }

  &__message {
    padding: 12px;
    margin-bottom: 16px;
    border-radius: @base-radius;
    background-color: var(--v-itemBackground-base);
    box-shadow: @base-shadow;

    &--bot {
      margin-right: 48px;
      background: #fff;
    }

    &--user {
      margin-left: 48px;
      background: #68cdff59;
    }

    &-content {
      line-height: 1.4;
      font-size: 16px;

      a {
        color: var(--v-primary-base);
        text-decoration: underline;
      }
    }
  }

  &__feedback {
    display: flex;
    margin: 16px 0;

    &-message {
      border-radius: @base-radius;
      background-color: var(--v-itemBackground-base);
      margin: 0 auto;
      display: flex;
      align-items: center;
      font-weight: 500;
      padding: 8px 16px;
      box-shadow: @base-shadow;
      font-size: 16px;
    }

    &-actions {
      margin-left: 12px;
    }
  }

  .v-textarea textarea {
    font-size: 16px !important;
    line-height: 1.4;
  }
}

/* Feedback animations */
.feedback-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.feedback-fade-leave-active {
  transition: none;
}

.feedback-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.feedback-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  transition: none;
}

.chatbot__feedback {
  will-change: transform, opacity;
  margin-top: 8px;
  margin-bottom: 8px;
  display: flex;
  justify-content: center;

  &-message {
    border-radius: @base-radius;
    border: 1px solid #ccc;
    background: #fff;
    margin: 0 auto;
    display: flex;
    align-items: center;
    font-weight: 600;
    padding: 8px 16px;
    box-shadow: @base-shadow;
  }

  &-actions {
    margin-left: 12px;
    display: flex;
    align-items: center;
  }
}
</style>
